<script setup>
import { computed, ref } from 'vue'
import Button from 'primevue/button'
import ColleaguesPickerPopover from './ColleaguesPickerPopover.vue'
import { getColleagueColor } from '../../../composables/useReservationsCalendar.js'
import { getInitials } from '../../../../../helpers.js'

const MAX_VISIBLE = 5

const props = defineProps({
    allEmployees:        { type: Array, default: () => [] },
    visibleColleagueIds: { type: Set,   default: () => new Set() },
})

const emit = defineEmits(['toggle-colleague'])

const visibleEmployees  = computed(() => props.allEmployees.slice(0, MAX_VISIBLE))
const hiddenColleagues  = computed(() => Math.max(0, props.allEmployees.length - MAX_VISIBLE))
const hasMoreColleagues = computed(() => hiddenColleagues.value > 0)

const pickerOpen   = ref(false)
const pickerAnchor = ref(null)
const pickerBtnRef = ref(null)

function openPicker() {
    pickerAnchor.value = pickerBtnRef.value?.$el?.getBoundingClientRect() ?? null
    pickerOpen.value = true
}
</script>

<template>
    <div v-if="allEmployees.length" class="flex flex-col gap-0 pb-1">
        <div class="flex items-center justify-between pb-1 px-1">
            <span class="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{{ $t('common.colleagues') }}</span>
        </div>

        <button
            v-for="(employee, i) in visibleEmployees"
            :key="employee.id"
            type="button"
            class="flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors w-full text-left"
            :class="visibleColleagueIds.has(employee.id) ? '' : 'opacity-40'"
            @click="emit('toggle-colleague', employee.id)"
        >
            <i
                class="!text-[12px] flex-shrink-0 w-4 transition-colors"
                :class="visibleColleagueIds.has(employee.id) ? 'pi pi-eye' : 'pi pi-eye-slash text-neutral-300'"
                :style="visibleColleagueIds.has(employee.id) ? { color: getColleagueColor(i) } : {}"
            />
            <div
                class="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold text-white flex-shrink-0"
                :style="{ background: getColleagueColor(i) }"
            >
                {{ getInitials(employee.name) }}
            </div>
            <span class="flex-1 text-[12.5px] text-neutral-800 dark:text-neutral-200 font-medium truncate min-w-0">
                {{ employee.name }}
            </span>
        </button>

        <div class="mt-0.5">
            <Button
                v-if="hasMoreColleagues"
                ref="pickerBtnRef"
                text
                size="small"
                @click="openPicker"
                class="w-full !justify-start !gap-1.5 !text-[11.5px] !font-medium !text-neutral-500 dark:!text-neutral-400 !rounded-lg"
            >
                <i class="pi pi-users !text-[10px]" />
                {{ $t('common.show_all') }}
                <span class="ml-auto bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-300 rounded px-1.5 py-px text-[10px] font-semibold">
                    +{{ hiddenColleagues }}
                </span>
            </Button>
        </div>
    </div>

    <ColleaguesPickerPopover
        :open="pickerOpen"
        :all-employees="allEmployees"
        :visible-colleague-ids="visibleColleagueIds"
        :anchor-rect="pickerAnchor"
        @close="pickerOpen = false"
        @toggle-colleague="(id) => emit('toggle-colleague', id)"
    />
</template>
